<template>
  <div>
    <draggable tag="ul"
               :list="schema"
               class="list-group dropArea"
               :class="!schema.length ? 'empty' : ''"
               :group="{
                  name: 'components'
               }"
               data-content="Drag and drop your fields here."
               @add="addItem">
      <li v-for="(item, index) in schema"
          :key="`drag-item-${item.name}`"
          :class="'list-group-item col-md-' +item.grid">
        <component
          v-if="componentsMapping[item.type]"
          :is="componentsMapping[item.type]"
          :item="item"
          :show-share-with-participant="!['row', 'divider'].includes(item.type)"
          :show-is-shared="!['row', 'divider'].includes(item.type)"
          :id="id"
          :shared-fields="sharedFields"
          @duplicate="duplicateField($event, index)"
          @remove="removeField($event, index)"
          @toggle="toggleField(item, $event)"
          @field-removed="$emit('field-removed', item.name)"
        />
      </li>
    </draggable>
  </div>
</template>

<script>
import Vue, { reactive, provide, set } from 'vue';
import draggable from 'vuedraggable';

import FormBuilderEditorHeader from "./editors/FormBuilderEditorHeader";
import FormBuilderEditorSubheader from "./editors/FormBuilderEditorSubheader";
import FormBuilderEditorField from "./editors/FormBuilderEditorField";
import FormBuilderEditorCheckbox from "./editors/FormBuilderEditorCheckbox";
import FormBuilderEditorCheckboxGroup from "./editors/FormBuilderEditorCheckboxGroup";
import FormBuilderEditorRadioGroup from "./editors/FormBuilderEditorRadioGroup";
import FormBuilderEditorTime from "./editors/FormBuilderEditorTime";
import FormBuilderEditorAccordion from "./editors/FormBuilderEditorAccordion";
import FormBuilderEditorCurrency from "./editors/FormBuilderEditorCurrency";
import FormBuilderEditorText from "./editors/FormBuilderEditorText";
import FormBuilderEditorFile from "./editors/FormBuilderEditorFile";
import FormBuilderEditorImage from "./editors/FormBuilderEditorImage";
import FormBuilderEditorSystemId from "./editors/FormBuilderEditorSystemId";
import FormBuilderEditorNumber from "./editors/FormBuilderEditorNumber";
import FormBuilderEditorRating from "./editors/FormBuilderEditorRating";
import FormBuilderEditorAddress from "./editors/FormBuilderEditorAddress";
import FormBuilderEditorSignature from "./editors/FormBuilderEditorSignature";
import FormBuilderEditorCaseManager from "./editors/FormBuilderEditorCaseManager";
import FormBuilderEditorSharedField from "./editors/FormBuilderEditorSharedField";
import FormBuilderEditorSharedFormPreview from "./editors/FormBuilderEditorSharedFormPreview";
import FormBuilderEditorSsn from "./editors/FormBuilderEditorSsn";
import FormBuilderEditorPhone from "./editors/FormBuilderEditorPhone";
import {FIELD_TYPE} from "../../enums/fieldType";

export default {
  components: {
    draggable,
  },

  props: {
    id: {
      type: Number,
      default: null
    },
    schema: {
      type: Array,
      required: true,
    },
    sharedFields: {
      type: Object,
      required: true,
    },
  },
  emits: ['field-removed'],

  setup(props, {emit}) {
    const openedFields = reactive({})

    provide('openedFields', openedFields)

    const COMPONENTS_MAPPING = {
      'header': FormBuilderEditorHeader,
      'text-entry': FormBuilderEditorSubheader,
      'divider': FormBuilderEditorField,
      'row': FormBuilderEditorField,
      'checkbox': FormBuilderEditorCheckbox,
      'checkbox-group': FormBuilderEditorCheckboxGroup,
      'select': FormBuilderEditorCheckboxGroup,
      'radio-group': FormBuilderEditorRadioGroup,
      'time': FormBuilderEditorTime,
      'date': FormBuilderEditorTime,
      'accordion': FormBuilderEditorAccordion,
      'currency': FormBuilderEditorCurrency,
      'text': FormBuilderEditorText,
      'password': FormBuilderEditorText,
      'email': FormBuilderEditorText,
      'phone': FormBuilderEditorPhone,
      'ssn': FormBuilderEditorSsn,
      'textarea': FormBuilderEditorText,
      'file': FormBuilderEditorFile,
      'image-upload': FormBuilderEditorImage,
      'systemid': FormBuilderEditorSystemId,
      'number': FormBuilderEditorNumber,
      'rating': FormBuilderEditorRating,
      'address': FormBuilderEditorAddress,
      'signature': FormBuilderEditorSignature,
      'select2': FormBuilderEditorCaseManager,
      'select_case_manager_secondary': FormBuilderEditorCaseManager,
      'shared-field': FormBuilderEditorSharedField,
      'shared-form-preview': FormBuilderEditorSharedFormPreview,
      'destination-account': FormBuilderEditorField,
      'programs-checkbox-group': FormBuilderEditorField,
    }

    const toggleField = (item, event) => {
      set(openedFields, item.name, event)
    }

    const removeField = (item, index) => {
      Vue.delete(props.sharedFields, item.name);
      props.schema.splice(index, 1);
      emit('field-removed', item.name)
    }

    const duplicateField = (item, index) => {
      let clone = JSON.parse(JSON.stringify(item));
      const sourceFieldName = clone.name
      let i = 0;
      clone.name = clone.type + '-' + Date.now();

      if (clone.type === 'accordion') {
        Object.keys(clone.values).map(obj => {
          clone.values[obj].name = clone.values[obj].type + '-' + Date.now() + i;
          i++;
        });
      }

      if (clone.type === FIELD_TYPE.SHARED_FIELD) {
        this.sharedFields[clone.name] = Object.assign({}, this.sharedFields[sourceFieldName])
      }

      // add element to schema
      props.schema.splice(index + 1, 0, clone);

      // open added element
      set(openedFields, clone.name, true)

      Vue.notify('Element duplicated!');
    }

    const addItem = (e) => {
      // fix for the last element
      const indexToOpen = e.newIndex === props.schema.length ? e.newIndex - 1 : e.newIndex;

      if (props.schema[indexToOpen]) {
        set(openedFields, props.schema[indexToOpen].name, true)
      }
      emit('add', e);
    }

    return {
      openedFields,
      duplicateField,
      removeField,
      toggleField,
      addItem,
      componentsMapping: COMPONENTS_MAPPING,
    }
  }
}
</script>

<style scoped lang="scss">

.dropArea {
  position: relative;
  background: #f5f5f5;
  min-height: 200px;
  overflow: hidden;
}

.dropArea.empty {
  opacity: 0.5;
  border: 1px dashed black;
  box-shadow: none;
}

.dropArea.empty:after {
  content: attr(data-content);
  position: absolute;
  text-align: center;
  top: 50%;
  left: 0;
  width: 100%;
}

.list-group-item > .collapse-item {
  cursor: move;
}
</style>



// WEBPACK FOOTER //
// VueFormEditor.vue?f6ce3fbc